/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment'
import React, { Component } from 'react'
import { useHistory } from 'react-router-dom'
import { getFullUrl } from '../../infrastructure/utils'
import { addQuerystring } from '../../utils/querystring'
import { Default, Desktop, Mobile, Tablet } from '../../utils/responsive'

import {
    ActionBar,
    ActionBarRow,
    GroupedSelectedFilters,
    Hits,
    HitsStats,
    InitialLoader,
    LayoutBody,
    LayoutResults,
    NoHits,
    PageSizeSelector,
    Pagination,
    RangeQuery,
    RefinementListFilter,
    ResetFilters,
    SearchBox,
    SearchkitManager,
    SearchkitProvider,
    Select,
    SideBar,
    TermQuery,
} from 'searchkit'
import Header from '../Header'

import AlwaysDisplayedCheckboxFilter from './components/AlwaysDisplayedCheckboxFilter'
import AxiosTransport from './components/AxiosTransport'
import {
    customHitStats,
    EventHitsTable,
    InitialLoaderComponent,
} from './components/Events'
import './components/Events.css'
import ExcludeCheckboxFilter from './components/ExcludeCheckboxFilter'
import SortTable from './components/SortTable'
import SumAggregationFilter from './components/SumAggregationFilter'
import TotalRow from './components/TotalRow'

const FilterButton = ({ toggleFilter }) => (
    <div className="btn btn-white btn-sm flex ai-center" onClick={toggleFilter}>
        <i className="fa fa-filter" style={{ fontSize: 20 }} />
        <span style={{ marginLeft: 5 }}>Filters</span>
    </div>
)

const FilterModal = ({ show, toggleFilter }) => (
    <div
        className="mobile-filters animated fadeIn"
        style={show ? {} : { display: 'none' }}
    >
        <div className="flex jc-space-between ai-center">
            <h3>Filters</h3>
            <i
                className="fa fa-times"
                style={{ fontSize: '2em' }}
                onClick={toggleFilter}
            />
        </div>
        <hr style={{ margin: '0 0 5px 0' }} />
        <AlwaysDisplayedCheckboxFilter
            id="past-events"
            title="History"
            label="Include past events?"
            // hack: this filter will always include everything
            filter={RangeQuery('eventStartYear', {
                gte: 1,
            })}
            showCount={false}
        />
        <AlwaysDisplayedCheckboxFilter
            id="test-events"
            title="Event type"
            label="Include test events?"
            // hack: this filter will always include everything
            filter={RangeQuery('eventStartYear', {
                gte: 1,
            })}
            showCount={false}
        />
        <RefinementListFilter
            id="brands"
            title="Brand"
            field="brand"
            size={2}
            operator="OR"
        />
        <RefinementListFilter
            id="venue"
            title="Venue"
            field="venueName.raw"
            size={5}
            operator="OR"
        />
        <RefinementListFilter
            id="event"
            title="Event name"
            field="name.raw"
            size={5}
            operator="OR"
        />
        <RefinementListFilter
            id="Year"
            title="Year"
            field="eventStartYear"
            size={5}
            orderKey="_term"
            orderDirection="desc"
            operator="OR"
        />
        <RefinementListFilter
            id="state"
            title="State"
            field="venueState"
            size={5}
            operator="OR"
        />
        <ExcludeCheckboxFilter
            id="categories"
            title="Excluded Categories"
            field="categories.raw"
            size={5}
            operator="OR"
        />
        <SumAggregationFilter field="ticketsAvailable" />
        <SumAggregationFilter field="ticketsSold" />
        <SumAggregationFilter field="ticketsSoldToday" />
        <SumAggregationFilter field="ticketsRemaining" />
        <SumAggregationFilter field="ticketsValue" />
    </div>
)

const CompareEvents = ({ events, removeAll, remove }) => {
    const history = useHistory()

    const show = events.length > 0
    if (!show) return null

    const redirect = () => {
        const newLocation = addQuerystring('/compare/sales', {
            e: events.map((e) => ({ id: e.eventId })),
        })

        history.push(newLocation)
    }

    return (
        <div style={{ border: '2px dashed lightgrey' }} className="m-sm p-xs">
            <div className="flex jc-space-between m-l-sm">
                <div>
                    <h4 style={{ color: '#337ab7' }}>Compare up to 4 events</h4>
                </div>
                <button className="btn btn-primary btn-xs" onClick={redirect}>
                    Compare
                </button>
            </div>
            <div className="flex ai-start">
                {events.map((e) => (
                    <div key={e.eventId} className="m-sm">
                        <span className="block">
                            <strong className="font-bold title">
                                {e.nameWithoutHighlight}
                            </strong>
                        </span>
                        <span className="text-muted text-xs block">
                            {moment.utc(e.eventStartDate).format('DD MMM YYYY')}{' '}
                            <br />
                            {e.venueNameWithoutHighlight} ({e.venueState})
                            <br />
                            <a
                                onClick={() => remove(e.eventId)}
                                className="actionlink"
                            >
                                <i
                                    className="fa fa-times m-r-xs"
                                    aria-hidden="true"
                                ></i>
                                Remove
                            </a>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

class EventsContainer extends Component {
    constructor(props) {
        super(props)
        this.setupSearchKit()
        this.state = { showFilters: false, eventsToCompare: [] }
    }

    setupSearchKit = () => {
        const searchUrl = getFullUrl('/api/events')
        this.searchkit = new SearchkitManager(searchUrl, {
            transport: new AxiosTransport(searchUrl),
        })

        this.searchkit.addDefaultQuery((query) => {
            const defaultSort = [{ eventStartDateUtc: { order: 'asc' } }]
            const pastEventsAccessor =
                this.searchkit.accessors.statefulAccessors['past-events']

            const testEventsAccessor =
                this.searchkit.accessors.statefulAccessors['test-events']

            const includePastEventsChecked =
                pastEventsAccessor == null
                    ? false
                    : pastEventsAccessor.state.value

            const includeTestEventsChecked =
                testEventsAccessor == null
                    ? false
                    : testEventsAccessor.state.value || false

            let currentQuery = query.setSort(defaultSort)

            if (!includePastEventsChecked) {
                currentQuery = currentQuery.addQuery(
                    RangeQuery('eventEndDateUtc', {
                        gt: 'now',
                    }),
                )
            }

            if (!includeTestEventsChecked) {
                currentQuery = currentQuery.addQuery(
                    TermQuery('isTestEvent', false),
                )
            }

            console.log(
                'EventsContainer -> setupSearchKit -> currentQuery',
                currentQuery,
            )
            return currentQuery
        })
    }

    toggleFilter = () => {
        this.setState((prev) => ({ showFilters: !prev.showFilters }))
    }

    addToCompare = (event) => {
        if (this.state.eventsToCompare.some((e) => e.eventId === event.eventId))
            return

        if (this.state.eventsToCompare.length === 4) return

        this.setState((prev) => ({
            eventsToCompare: [...prev.eventsToCompare, event].sort((a, b) =>
                moment(a.eventStartDate).diff(moment(b.eventStartDate)),
            ),
        }))
    }

    removeAllFromCompare = () => {
        this.setState({
            eventsToCompare: [],
        })
    }

    removeFromCompare = (eventId) => {
        this.setState((prev) => ({
            eventsToCompare: prev.eventsToCompare.filter(
                (e) => e.eventId !== eventId,
            ),
        }))
    }

    render() {
        return (
            <SearchkitProvider searchkit={this.searchkit}>
                <div>
                    <Header
                        search={
                            <SearchBox
                                autofocus
                                searchOnChange
                                queryFields={['name', 'eventId', 'venueName']}
                                queryOptions={{ default_operator: 'AND' }}
                            />
                        }
                    />
                    <LayoutBody>
                        <Desktop>
                            <SideBar>
                                <h4>Refine Results</h4>
                                <AlwaysDisplayedCheckboxFilter
                                    id="past-events"
                                    title="History"
                                    label="Include past events?"
                                    // hack: this filter will always include everything
                                    filter={RangeQuery('eventStartYear', {
                                        gte: 1,
                                    })}
                                    showCount={false}
                                />
                                <AlwaysDisplayedCheckboxFilter
                                    id="test-events"
                                    title="Event type"
                                    label="Include test events?"
                                    // hack: this filter will always include everything
                                    filter={RangeQuery('eventStartYear', {
                                        gte: 1,
                                    })}
                                    showCount={false}
                                />
                                <RefinementListFilter
                                    id="brands"
                                    title="Brand"
                                    field="brand"
                                    size={2}
                                    operator="OR"
                                />
                                <RefinementListFilter
                                    id="venue"
                                    title="Venue"
                                    field="venueName.raw"
                                    size={5}
                                    operator="OR"
                                />
                                <RefinementListFilter
                                    id="event"
                                    title="Event name"
                                    field="name.raw"
                                    size={5}
                                    operator="OR"
                                />
                                <RefinementListFilter
                                    id="Year"
                                    title="Year"
                                    field="eventStartYear"
                                    size={5}
                                    orderKey="_term"
                                    orderDirection="desc"
                                    operator="OR"
                                />
                                <RefinementListFilter
                                    id="state"
                                    title="State"
                                    field="venueState"
                                    size={5}
                                    operator="OR"
                                />
                                <ExcludeCheckboxFilter
                                    id="categories"
                                    title="Excluded Categories"
                                    field="categories.raw"
                                    size={5}
                                    operator="OR"
                                />
                                <SumAggregationFilter field="ticketsAvailable" />
                                <SumAggregationFilter field="ticketsSold" />
                                <SumAggregationFilter field="ticketsSoldToday" />
                                <SumAggregationFilter field="ticketsRemaining" />
                                <SumAggregationFilter field="ticketsValue" />
                            </SideBar>
                        </Desktop>

                        <LayoutResults>
                            <CompareEvents
                                events={this.state.eventsToCompare}
                                removeAll={this.removeAllFromCompare}
                                remove={this.removeFromCompare}
                            />
                            <ActionBar>
                                <ActionBarRow>
                                    <HitsStats component={customHitStats} />

                                    <Mobile>
                                        <FilterButton
                                            toggleFilter={this.toggleFilter}
                                        />
                                        <FilterModal
                                            show={this.state.showFilters}
                                            toggleFilter={this.toggleFilter}
                                        />
                                    </Mobile>
                                    <Tablet>
                                        <FilterButton
                                            toggleFilter={this.toggleFilter}
                                        />
                                        <FilterModal
                                            show={this.state.showFilters}
                                            toggleFilter={this.toggleFilter}
                                        />
                                    </Tablet>
                                    <Desktop>
                                        <div className="flex ai-center">
                                            <ResetFilters />
                                            <PageSizeSelector
                                                options={[
                                                    10, 16, 20, 24, 30, 50, 100,
                                                    150, 200,
                                                ]}
                                                listComponent={Select}
                                            />
                                        </div>
                                    </Desktop>
                                </ActionBarRow>

                                <ActionBarRow>
                                    <GroupedSelectedFilters />
                                </ActionBarRow>
                            </ActionBar>

                            <div className="m-xs">
                                <div className="table-responsive">
                                    <table className="event-table table table-striped">
                                        <thead>
                                            <SortTable
                                                options={[
                                                    {
                                                        label: 'Name',
                                                        field: 'name.raw',
                                                    },
                                                    {
                                                        label: 'Date',
                                                        field: 'eventStartDate',
                                                        defaultOption: true,
                                                    },
                                                    {
                                                        label: 'Venue',
                                                        field: 'venueName.raw',
                                                    },
                                                    {
                                                        label: 'Available',
                                                        field: 'ticketsAvailable',
                                                        className:
                                                            'table-number',
                                                    },
                                                    {
                                                        label: 'Sold',
                                                        field: 'ticketsSold',
                                                        className:
                                                            'table-number',
                                                    },
                                                    {
                                                        label: 'Sold Today',
                                                        field: 'ticketsSoldToday',
                                                        className:
                                                            'table-number',
                                                    },
                                                    {
                                                        label: 'Remaining',
                                                        field: 'ticketsRemaining',
                                                        className:
                                                            'table-number',
                                                    },
                                                    {
                                                        label: 'Sale Value',
                                                        field: 'ticketsValue',
                                                        className:
                                                            'table-number',
                                                    },
                                                    {
                                                        label: '% Sold',
                                                        field: 'percentSold',
                                                        className:
                                                            'table-number',
                                                    },
                                                ]}
                                            />
                                            <TotalRow />
                                        </thead>
                                        <Hits
                                            hitsPerPage={50}
                                            highlightFields={[
                                                'name',
                                                'venueName',
                                            ]}
                                            sourceFilter={[
                                                'name',
                                                'eventId',
                                                'imageUrl',
                                                'venueName',
                                                'venueTimezone',
                                                'venueState',
                                                'eventStartDate',
                                                'eventStartYear',
                                                'ticketsAvailable',
                                                'ticketsSold',
                                                'ticketsSoldToday',
                                                'ticketsRemaining',
                                                'ticketsValue',
                                                'percentSold',
                                                'categories',
                                            ]}
                                            listComponent={(tableProps) => (
                                                <EventHitsTable
                                                    {...tableProps}
                                                    addToCompare={
                                                        this.addToCompare
                                                    }
                                                />
                                            )}
                                            scrollTo="body"
                                            mod="grid"
                                        />
                                    </table>
                                </div>
                            </div>
                            <NoHits suggestionsField={'name'} />
                            <InitialLoader component={InitialLoaderComponent} />
                            <hr />
                            <Default>
                                <Pagination showNumbers />
                            </Default>
                            <Mobile>
                                <Pagination />
                            </Mobile>
                        </LayoutResults>
                    </LayoutBody>
                </div>
            </SearchkitProvider>
        )
    }
}
export default EventsContainer
